import { useMemo } from 'react';
import {
  subscriptionApiKeys,
  useSubscriptionApi,
} from '@hooks/api/account/useSubscriptionApi';
import { useGetApi } from '@hooks/api/useGetApi';
import type {
  ActiveSubscriptionItem,
  SaasProduct,
} from 'types/account/subscriptions';
import type { GetApiOptions } from 'types/api';

export function useGetSaaSProducts(
  activeSubscriptions: ActiveSubscriptionItem[],
  options?: Partial<GetApiOptions<SaasProduct[], Error>>
) {
  const api = useSubscriptionApi();

  const { data, ...results } = useGetApi({
    queryKey: subscriptionApiKeys.getSaaSProducts(),
    request: () => api.getSaaSProducts(),
    ...options,
  });

  const activeSkus = useMemo(
    () =>
      activeSubscriptions.flatMap((subscription) =>
        subscription.items.map((item) => item.price.sku)
      ),
    [activeSubscriptions]
  );

  const activeSuiteProduct = useMemo(() => {
    return data?.find(
      (product) =>
        activeSkus.includes(product.sku) &&
        product.categories?.includes('suite')
    );
  }, [data, activeSkus]);
  return {
    ...results,
    activeSuite: activeSubscriptions
      ?.find((subscription) =>
        subscription.items.find(
          (item) => item.price.sku === activeSuiteProduct?.sku
        )
      )
      ?.items.find((item) => item.price.sku === activeSuiteProduct?.sku),
    allProducts: data ?? [],
    hardwareProducts:
      data?.filter((product) => product.categories?.includes('hardware')) ?? [],
    products:
      data?.filter((product) => !activeSkus.includes(product.sku)) ?? [],
    serviceProducts:
      data?.filter(
        (product) =>
          product.categories?.includes('service') &&
          !product.categories?.includes('suite')
      ) ?? [],
    suites:
      data
        ?.filter((product) => product.categories?.includes('suite'))
        .sort(
          (a, b) =>
            (a.prices.find((price) => price.frequency === 'monthly')?.price ??
              0) -
            (b.prices.find((price) => price.frequency === 'monthly')?.price ??
              0)
        ) ?? [],
  };
}
